import {Component, Expressions, Plugins} from '@packages/systems/wfdl';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {isPinnedToParent} from '@packages/systems/constraints';
import {
  findElement,
  findFurthestAncestor,
  getChildren,
  getClosestFigureElement,
  getClosestRichTextElement,
  hasOrphanRequiredOnlyOnceInElement,
  hasRequiredOnlyOnceInSite,
  isComponentRoot,
  tabsPair,
  getOptimizationExperienceElementForBaseChild,
} from '@packages/utilities/platform';
import {
  isBody,
  isCell,
  isDeletedUserFormCustomField,
  isGenericUserInput,
  isHtml,
  isRequiredOnlyOnceInElement,
  isRequiredOnlyOnceInSite,
  isTabsPair,
  isTextHolder,
  isTypographic,
  isUserAccountForm,
  isUserSignUpForm,
} from '@packages/utilities/elementTypeUtils';
import {weakMemo} from '@packages/utilities/memo';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {
  getLiveComponentMap,
  getPluginsFromState,
  selectCurrentComponent,
  selectIsSlotDefaultSelection,
  selectSlotDefaultElementFromSelection,
} from '@packages/utilities/platform-selectors';
import {Err, Ok, type Result} from '@packages/utilities/fp/result';
import * as DesignerErrorCodes from '@packages/systems/core/constants/ErrorCodes';
import {getGuidNullable} from '@packages/systems/core/utils/NativeIdUtils';
import {selectIsCanvasFocused} from '@packages/systems/designer-selectors';
import {maybe} from '@packages/utilities/fp/option';
import {identity} from '@packages/utilities/fp/utils';
import {selectDesignerAbility} from '@packages/systems/designer-abilities';
import {canRemoveElementInPageBuildMode} from '@packages/utilities/PageBuildUtils';

const shouldMoveParentInstead =
  (plugins: Plugins.PluginsType) => (parent: ExpressionElement) => {
    const children = getChildren(parent);
    return (
      children &&
      children.every((element) => isPinnedToParent(element, plugins))
    );
  };

/**
 * Determines which element should be the target of a drag operation in the Designer.
 * This function handles special cases where the intended target should be a parent or container element.
 *
 * @param root - The root component containing the element tree
 * @param plugins - The plugins configuration object containing atom definitions
 * @param element - The element that was initially targeted
 *
 * @returns The element that should actually be targeted, which may be:
 * - The original element
 * - A parent figure element (for rich text)
 * - A parent element if all its children are pinned to it
 *
 * @example
 * ```typescript
 * const targetElement = proxyElement(rootComponent)(plugins)(selectedElement);
 * ```
 */
export const proxyElement =
  (root: Component.ComponentType) =>
  (plugins: Plugins.PluginsType) =>
  (element: ExpressionElement): ExpressionElement => {
    const rootRender = Component.getRender(root);

    if (rootRender === null) return element;

    if (getClosestRichTextElement(rootRender, element.id)) {
      const closestFigureElement = getClosestFigureElement(
        rootRender,
        element.id
      );

      if (closestFigureElement) return closestFigureElement;
    }

    const parent = Expressions.getParent(rootRender, element.id);
    if (parent && shouldMoveParentInstead(plugins)(parent)) {
      return proxyElement(root)(plugins)(parent);
    }
    return element;
  };

/**
 * Checks if an element can be moved. A good example of a non-movable element would be the body.
 */
export const isMovable = (
  plugins: Plugins.PluginsType,
  componentMap: Component.ComponentMap,
  element: ExpressionElement
): boolean =>
  !isBody(element) &&
  !isHtml(element) &&
  !isComponentRoot(componentMap)(element) &&
  !isPinnedToParent(element, plugins);
/**
 * When clipping either a Tab Link or Tab Pane, we do something exotic and
 * treat these elements as a pair, meaning: when one is clipped, we also clip the other.
 *
 * This type represents a selection of either the Link or the Pane, along with the pair associated with the selection.
 */
type ClippedTabPairData = {
  type: 'tabs-pair';
  /**
   * The element that was selected for clipping
   */
  clippedElement: ExpressionElement;
  /**
   * The tab link pane elements associated with the clipped element
   */
  tabsPair: [tabLink: ExpressionElement, tabPane: ExpressionElement];
};
/**
 * When clipping a Cell, it is a special case and we clip all of its children instead.
 */
type ClippedCellData = {
  type: 'cell';
  /**
   * The element that was selected for clipping
   */
  clippedElement: ExpressionElement;
  /**
   * The children of the clipped element
   */
  children: ExpressionElement[];
};
/**
 * Represents a normal, single clipped element
 */
type ClippedElementData = {
  type: 'element';
  /**
   * The element that was selected for clipping
   */
  clippedElement: ExpressionElement;
};
type AnyClippedElementData =
  | ClippedTabPairData
  | ClippedCellData
  | ClippedElementData;
type ElementClipResult = Result<
  DesignerErrorCodes.ErrorCode,
  AnyClippedElementData[]
>;
/**
 * Given an array of clipped elements data, validates there is exactly one item before attempting
 * to continue with an operation that expects a single item.
 *
 * While we migrate element clipping and all the associated functionalities like cut/copy/delete
 * to work with multi-item selections, this function is used for backwards compatibility. It is
 * temporary and will ultimatly be removed.
 *
 * Use this with `.chain()` calls to gracefully stop in code paths that were
 * not yet migrated to support multiple items.
 */
export const unwrapSingleClippedElementResult = (
  clippedElementsData: AnyClippedElementData[]
): Result<DesignerErrorCodes.ErrorCode, ExpressionElement> => {
  if (clippedElementsData.length > 1)
    return Err(DesignerErrorCodes.RequiresSingleItemSelection);

  const maybeSingleClipResult = clippedElementsData[0];
  if (!maybeSingleClipResult) return Err(DesignerErrorCodes.ElementMissing);

  return Ok(maybeSingleClipResult.clippedElement);
};

const _getElementsToClip = (isRemoval: boolean) =>
  weakMemo((state: DesignerStores): ElementClipResult => {
    const {UiNodeStore, DesignerStore, SiteUsersStore} = state;
    const plugins = getPluginsFromState(state);

    // Guard against clipping multiple items. This will be updated as part of multi-editing
    // once we begin enabling operations on selections.
    // We don't expect multiple items in the selection set while the feature flag is disabled!
    if (UiNodeStore.selectionSet.size > 1) {
      return Err(DesignerErrorCodes.RequiresSingleItemSelection);
    }

    // Validate that the element exists
    // TODO: Implement in support of multiple items
    // https://webflow.atlassian.net/browse/DESUX-9540
    const elementNativeId = UiNodeStore.selectedNodeNativeId;

    if (!elementNativeId) {
      return Err(DesignerErrorCodes.ElementMissing);
    }

    const elementId = getGuidNullable(elementNativeId);

    if (!elementId) {
      return Err(DesignerErrorCodes.ElementMissing);
    }

    const currentComponent = selectCurrentComponent({
      DesignerStore,
      UiNodeStore,
    });

    const componentRender = Component.getRender(currentComponent);

    const renderElement = Expressions.getElementById(
      componentRender,
      elementId
    );

    const slotDefaultElement =
      !renderElement && !isRemoval && selectIsSlotDefaultSelection(state)
        ? selectSlotDefaultElementFromSelection(state)
        : null;

    const selectedElement = renderElement ?? slotDefaultElement;

    if (!selectedElement) {
      return Err(DesignerErrorCodes.ElementMissing);
    }

    // Validate that the canvas is focused
    if (!selectIsCanvasFocused(state)) {
      return Err(DesignerErrorCodes.CanvasNotFocused);
    }

    if (slotDefaultElement) {
      return Ok([{type: 'element', clippedElement: slotDefaultElement}]);
    }

    if (isCell(selectedElement)) {
      if (isRemoval) {
        const children = getChildren(selectedElement);

        if (!children || children.length === 0) {
          return Err(DesignerErrorCodes.UnmovableElement(selectedElement));
        }

        return Ok([
          {
            type: 'cell',
            clippedElement: selectedElement,
            children,
          },
        ]);
      } else {
        // Early return the selectedElement instead of the proxyElement (which would be the parent QuickStack element) if it is a Cell
        return Ok([
          {
            type: 'element',
            clippedElement: selectedElement,
          },
        ]);
      }
    }

    type TabsPair = [ExpressionElement, ExpressionElement] | null;
    if (isTabsPair(selectedElement)) {
      const maybeTabsPair = maybe<TabsPair>(null)(
        identity<NonNullable<TabsPair>>
      )(tabsPair(selectedElement)(componentRender));
      if (!maybeTabsPair) return Err(DesignerErrorCodes.ElementMissing);

      return Ok([
        {
          type: 'tabs-pair',
          clippedElement: selectedElement,
          tabsPair: maybeTabsPair,
        },
      ]);
    }

    // Find the appropriate proxy element
    let element;

    if (isRemoval) {
      if (isTypographic(selectedElement)) {
        element = selectedElement;
      } else {
        // Some elements need to remove their proxy element
        element = proxyElement(currentComponent)(plugins)(selectedElement);
      }

      // error if attempting to remove a component instance outside of a page slot in build mode
      const isInPageBuildMode =
        selectDesignerAbility('isInPageBuildMode')(state);
      const canAddComponentsInStaticRichText = selectDesignerAbility(
        'canAddComponentsInStaticRichText'
      )(state);

      const components = DesignerStore.components;

      if (
        isInPageBuildMode &&
        !canRemoveElementInPageBuildMode(
          element,
          components,
          canAddComponentsInStaticRichText
        )
      ) {
        return Err(DesignerErrorCodes.RequiredOnPage);
      }
    } else {
      element = proxyElement(currentComponent)(plugins)(
        findFurthestAncestor(componentRender, selectedElement, isTextHolder) ||
          selectedElement
      );
    }

    // Validate error conditions based on the (proxy) element
    if (isRequiredOnlyOnceInSite(element)) {
      return Err(DesignerErrorCodes.OneInstanceRequired);
    }

    if (isRequiredOnlyOnceInElement(element)) {
      return Err(DesignerErrorCodes.OneInstancePerComponent);
    }

    if (
      hasRequiredOnlyOnceInSite(element) ||
      hasOrphanRequiredOnlyOnceInElement(element)
    ) {
      return Err(DesignerErrorCodes.ContainsRequiredElement);
    }

    if (!isMovable(plugins, getLiveComponentMap(DesignerStore), element)) {
      return Err(DesignerErrorCodes.UnmovableElement(element));
    }

    if (
      findElement(element, isGenericUserInput) &&
      !isDeletedUserFormCustomField(element, SiteUsersStore) &&
      // dont prevent delete if the selected element is an ancestor of a form (rather than within it)
      !findElement(
        element,
        (el) => isUserSignUpForm(el) || isUserAccountForm(el)
      )
    ) {
      return Err(DesignerErrorCodes.UndeletableUserField);
    }

    if (isRemoval) {
      const optimizationExperienceElement =
        getOptimizationExperienceElementForBaseChild(element, componentRender);
      if (optimizationExperienceElement) {
        element = optimizationExperienceElement;
      }
    }

    return Ok([
      {
        type: 'element',
        clippedElement: element,
      },
    ]);
  });

export const getElementsToClip = _getElementsToClip(false);
export const getElementsToClipForRemoval = _getElementsToClip(true);
